import React, {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { HeadTag } from 'shared/components';
import FooterWrapper from './FooterWrapper';
import Footer from './components/Footer';
import DetailNotExpectedPage from './components/DetailNotExpected';
import { Formik, FormikProps } from 'formik';
import {
    RawData,
    RawDocumentClient,
} from '../listing-packets/components/VirtualizedTable';
import { useLocation, useNavigate } from 'react-router-dom';
import { ManagePacketService } from 'services/ManagePacketService';
import { Toast, Utils } from 'shared/helpers';
import { NotExpectedPacketScheme } from 'validators/SinglePacketScheme';
import { formattingAction } from 'shared/helpers/FormatAction';
import { useDispatch, useSelector } from 'react-redux';
import { inferCompletedModal } from '../single-fix-packet/components/CompletedModal';
import { LOCATION_CHANGE } from 'redux-first-history';
import { Action } from 'history';
import { useLeavePrompt } from 'shared/helpers/UseExitPrompt';
import {
    useEvent,
    usePageLeave,
    useTimeoutFn,
    useUpdateEffect,
} from 'react-use';
import { SocketActions } from 'state/socket/Action';
import { useFormikElementOnValidationEffect } from 'shared/helpers/Formik';

import DateFnsAdapter from '@date-io/date-fns';
import { ManagePacketEnd } from 'shared/Constants';
import { BroadcastActions } from 'state/broadcast/Action';
import { CarryAppActions } from 'state/carriesapplication/Action';
import { AxiosError } from 'axios';
import isPointerInside from 'shared/helpers/isPointerInside';

const dateFns = new DateFnsAdapter();

export const NotExpectedMeta = React.createContext<NotExpectedContext | null>(
    null
);

const NotExpectedPage = (): ReactElement => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const socketState = useSelector((state: any) => state.socketState);
    const profileState = useSelector((state: any) => state.profileState);
    const defaultState = location.state as RawData;
    const innerFormik = useRef<FormikProps<FormikPropsFixPacketValues> | null>(
        null
    );
    const innerMainRef = useRef<HTMLDivElement | null>(null);
    const [isActiveBackNavigation, setActiveBackNavigation] =
        useState<boolean>(false);
    const [isCommonHighlight, setCommonHighlight] = useState<boolean>(false);
    const [isFreeze, setFreezing] = useState<boolean>(false);

    const [isToSubmit, setSubmited] = React.useState<boolean>(false);

    const completedModalRef = React.useRef<inferCompletedModal | null>(null);
    const isGroupingFailed = useMemo(
        () =>
            !!(defaultState && defaultState.packetStatus === 'Grouping Failed'),
        [defaultState]
    );
    const timeToRework = useMemo(
        () =>
            defaultState && defaultState.timeToRework
                ? defaultState.timeToRework
                : null,
        [location.state]
    );

    const isUpdateCategory = useMemo(
        () =>
            defaultState && defaultState.updateCategory
                ? defaultState.updateCategory
                : false,
        [defaultState]
    );
    const showCategory = useMemo(
        () =>
            defaultState && defaultState.showCategory
                ? defaultState.showCategory
                : false,
        [defaultState]
    );
    const currentIdx = useMemo(
        () =>
            window.history.state &&
            typeof window.history.state.idx !== 'undefined'
                ? window.history.state.idx
                : null,
        []
    );
    const isPrimaryRoute = useMemo(
        () => typeof currentIdx === 'number' && currentIdx === 0,
        [currentIdx]
    );
    const clientId = useMemo(
        () =>
            defaultState && defaultState.clientId ? defaultState.clientId : '',
        [defaultState]
    );
    /** @constant {string} - clientId from action uuid in packet data */
    const packetId = useMemo(
        () =>
            defaultState && defaultState.actions && defaultState.actions.uuid
                ? defaultState.actions.uuid
                : '',
        [defaultState]
    );

    const isPageLeave = useSelector(
        (state: any) => state.carryState.isPageLeave
    );
    const recordLocations = useSelector(
        (state: any) => state.persistState.recordLocations
    );
    const isRecordLocale = useMemo(
        () =>
            recordLocations &&
            recordLocations &&
            recordLocations.filter(
                (e: any) =>
                    e.location.pathname === '/not-expected-cross-check' &&
                    e &&
                    e.location.state.actions &&
                    e.location.state.actions.uuid &&
                    e.location.state.actions.uuid === packetId
            ).length > 1 &&
            isPageLeave,
        [isPageLeave, packetId, recordLocations]
    );

    const getReducementInProcessedDocument = React.useCallback(
        (prev: any[], current: any) => {
            const arrayDocument = prev;
            // eslint-disable-next-line no-underscore-dangle
            if (arrayDocument.some((i) => i._id === current._id) === false) {
                arrayDocument.push(current);
            }

            return arrayDocument;
        },
        []
    );
    const isAllowingProceedReValidation = useMemo(() => {
        if (defaultState) {
            return typeof defaultState.canProceedValidation === 'boolean'
                ? defaultState.canProceedValidation
                : false;
        }
        return false;
    }, [defaultState]);
    const isAllowingGroupUpdate = useMemo(() => {
        if (defaultState) {
            return typeof defaultState.allowGroupingUpdate === 'boolean' &&
                isGroupingFailed
                ? defaultState.allowGroupingUpdate
                : false;
        }
        return false;
    }, [defaultState, isGroupingFailed]);
    const isAllowingGroupUpdateGlobal = useMemo(() => {
        if (defaultState) {
            return typeof defaultState.allowGroupingUpdate === 'boolean'
                ? defaultState.allowGroupingUpdate
                : false;
        }
        return false;
    }, [defaultState]);
    const isAllowingRerunGrouping = useMemo(() => {
        if (defaultState) {
            return typeof defaultState.canProceedGrouping === 'boolean'
                ? defaultState.canProceedGrouping
                : false;
        }
        return false;
    }, [defaultState]);
    const isDuplicateDocumentsFound = useMemo(
        () =>
            defaultState &&
            typeof defaultState.isDuplicateDocumentsFound === 'boolean'
                ? defaultState.isDuplicateDocumentsFound
                : false,
        [location.state]
    );

    const showDuplicateDocuments = useMemo(
        () => (defaultState ? defaultState.showDuplicateDocuments : false),
        [location.state]
    );

    /** @constant {RawDocument[]} - documents from  packet data */
    const documents: RawDocumentClient[] = useMemo(
        () =>
            defaultState && defaultState.documents
                ? Array.from(defaultState.documents)
                      .filter(
                          (e) =>
                              ((e.isDuplicate === false &&
                                  isUpdateCategory === true) ||
                                  isUpdateCategory === false) &&
                              ((e.isDuplicate === false &&
                                  isAllowingGroupUpdateGlobal === true) ||
                                  isAllowingGroupUpdateGlobal === false) &&
                              ((e.isDuplicate === false &&
                                  isDuplicateDocumentsFound === true &&
                                  showDuplicateDocuments === true) ||
                                  isDuplicateDocumentsFound === false ||
                                  showDuplicateDocuments === false)
                      )
                      .sort((a, b) =>
                          String(`${a.category}`).localeCompare(b.category)
                      )
                      .map((el) => ({
                          ...el,
                          groups:
                              el.group === 'common'
                                  ? []
                                  : [`Group ${el.group}`],
                          sendToRework: false,
                          statusMessage: '',
                          predefinedMessage: '',
                          actions: formattingAction(el),
                      }))
                      .reduce(getReducementInProcessedDocument, [])
                : [],
        [
            defaultState,
            getReducementInProcessedDocument,
            isAllowingGroupUpdateGlobal,
            isDuplicateDocumentsFound,
            isUpdateCategory,
            showDuplicateDocuments,
        ]
    );
    /** @constant {RawDocument[]} - documents from  packet data */
    const documentsDuplication: RawDocumentClient[] = useMemo(
        () =>
            defaultState && defaultState.documents
                ? Array.from(defaultState.documents)
                      .filter((e) => e.isDuplicate === true)
                      .sort((a, b) =>
                          String(`${a.category}`).localeCompare(b.category)
                      )
                      .map((el) => ({
                          ...el,
                          groups:
                              el.group === 'common'
                                  ? []
                                  : [`Group ${el.group}`],
                          sendToRework: false,
                          statusMessage: '',
                          predefinedMessage: '',
                          actions: formattingAction(el),
                      }))
                      .reduce(getReducementInProcessedDocument, [])
                : [],
        [defaultState, getReducementInProcessedDocument]
    );
    const isAllowRerunOnSavingPacket = useMemo(
        () =>
            defaultState &&
            (defaultState.packetStatus === 'Duplication Found' ||
                defaultState.packetStatus === 'Fully Duplication' ||
                defaultState.packetStatus === 'Partial Duplication' ||
                defaultState.packetStatus === 'Duplicate - New docs') &&
            defaultState.isDuplicate,
        [defaultState]
    );

    const groups = useMemo(
        () =>
            defaultState
                ? Array.from({ length: defaultState.totalGroups }).map(
                      (e, i) => `Group ${i + 1}`
                  )
                : [],
        [defaultState]
    );
    const isDocumentHaveUnexpectedType = useMemo(
        () => documents.some((e) => e.unexpectedFileType === true),
        [documents]
    );

    const [isLoading, setLoading] = useState<boolean>(false);

    // const getFilteredDocuments = React.useCallback(
    //     (_documents: RawDocument[]): RawDocument[] =>
    //         _documents.filter(
    //             (o, i) =>
    //                 Array.isArray(documents) &&
    //                 documents.length > 0 &&
    //                 o.category !== documents[i].category
    //         ),
    //     [documents]
    // );

    /**
     * Handle formik submit
     * @param {FormikPropsFixPacketValues} values - formik values.
     */
    const handleFormikSubmit = React.useCallback(
        (values: FormikPropsFixPacketValues) => {
            setLoading(true);
            const initialDocument =
                innerFormik.current?.initialValues.documents;

            ManagePacketService.updateCategory({
                clientId,
                packetId,
                documents: Array.from(values.documents).map((el, index) => {
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        actions: noaction,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        processedFile: b,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        group: g,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groupName: gi,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        allowReplaceDocument: ar,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        unexpectedFileType: x,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groups: gx,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        isDuplicate: j,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        variation: o,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        sendToRework: so,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        predefinedMessage: sp,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        statusMessage: sm,
                        ...document
                    } = el;

                    return {
                        ...document,
                        categoryUpdated: initialDocument
                            ? initialDocument[index].category !== el.category
                            : false,
                    };
                }),
            })
                .then((response: any) => {
                    if (
                        response &&
                        Array.isArray(response) &&
                        response.length > 0
                    ) {
                        Utils.handleSuccessResponse(response[0]);
                    }
                    setLoading(false);

                    completedModalRef.current?.setOpen();
                    // navigate('/manage-cross-checks');
                })
                .catch((error) => {
                    setLoading(false);
                    Utils.handleErrorResponse(error);
                });
        },

        [clientId, packetId]
    );
    /**
     * Handle formik submit
     * @param {FormikPropsFixPacketValues} values - formik values.
     */
    const handleFormikUpdateGroup = React.useCallback(
        (values: FormikPropsFixPacketValues) => {
            setLoading(true);
            const initialDocument =
                innerFormik.current?.initialValues.documents;

            ManagePacketService.updateGrouping({
                clientId,
                packetId,
                documents: Array.from(values.documents).map((el, index) => {
                    const {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        actions: noaction,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        processedFile: b,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        group: g,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groupName: gi,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        allowReplaceDocument: ar,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        unexpectedFileType: x,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        isDuplicate: j,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        variation: o,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        category: c,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        categoryUpdated: cu,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        groups: gj,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        sendToRework: so,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        predefinedMessage: sp,
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        statusMessage: sm,
                        ...document
                    } = el;
                    return {
                        ...document,
                        group:
                            g === 'common' && gj.length === 0
                                ? ['common']
                                : Array.from(gj).map((e) =>
                                      e.replace('Group ', '')
                                  ),
                        category: c,
                        groupUpdated: initialDocument
                            ? initialDocument[index].groups.join(',') !==
                              gj.join(',')
                            : false,
                    };
                }),
            })
                .then((response: any) => {
                    if (
                        response &&
                        Array.isArray(response) &&
                        response.length > 0
                    ) {
                        Utils.handleSuccessResponse(response[0]);
                    }
                    setLoading(false);
                    completedModalRef.current?.setOpen();
                    // navigate('/manage-packets');
                })
                .catch((error) => {
                    setLoading(false);
                    Utils.handleErrorResponse(error);
                });
        },

        [clientId, packetId]
    );
    const handleSaveMoveToCategorization = React.useCallback(() => {
        setLoading(true);

        ManagePacketService.moveToCategorization({
            packetId,
        })
            .then((response: any) => {
                if (
                    response &&
                    Array.isArray(response) &&
                    response.length > 0
                ) {
                    Utils.handleSuccessResponse(response[0]);
                }
                setLoading(false);

                completedModalRef.current?.setOpen();
                // navigate('/manage-packets');
            })
            .catch((error) => {
                setLoading(false);
                Utils.handleErrorResponse(error);
            });
    }, [packetId]);

    const handleSaveForReRunPacket = React.useCallback(() => {
        setLoading(true);
        setActiveBackNavigation(true);
        Promise.all([
            ManagePacketService.rerunPacket({
                packetId,
            }),
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId,
            }),
        ])
            .then((response) => {
                setLoading(false);
                response.forEach((element) => {
                    if (element.config.url === ManagePacketEnd.rerun_PACKET)
                        Utils.handleSuccessResponse(element);
                });
                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setLoading(false);
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [isPrimaryRoute, navigate, packetId]);

    const handleSaveForReRunGrouping = React.useCallback(() => {
        setLoading(true);
        setActiveBackNavigation(true);
        Promise.all([
            ManagePacketService.rerunGrouping({
                packetId,
            }),
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId,
            }),
        ])
            .then((response) => {
                setLoading(false);
                response.forEach((element) => {
                    if (element.config.url === ManagePacketEnd.rerun_GROUPING)
                        Utils.handleSuccessResponse(element);
                });
                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setLoading(false);
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [isPrimaryRoute, navigate, packetId]);

    const handleSaveForReRunDuplicationPacket = React.useCallback(() => {
        setLoading(true);
        setActiveBackNavigation(true);
        Promise.all([
            ManagePacketService.rerunDuplicate({
                packetId,
            }),
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId,
            }),
        ])
            .then((response) => {
                setLoading(false);
                response.forEach((element) => {
                    if (element.config.url === ManagePacketEnd.rerun_DUPLICATE)
                        Utils.handleSuccessResponse(element);
                });
                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setLoading(false);
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [isPrimaryRoute, navigate, packetId]);
    const handleSaveForRevalidation = React.useCallback(() => {
        setLoading(true);
        setActiveBackNavigation(true);
        Promise.all([
            ManagePacketService.rerunValidation({
                packetId,
            }),
            ManagePacketService.patchOperatorAction({
                open: false,
                packetId,
            }),
        ])
            .then((response) => {
                setLoading(false);
                response.forEach((element) => {
                    if (element.config.url === ManagePacketEnd.rerun_VALIDATION)
                        Utils.handleSuccessResponse(element);
                });

                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setLoading(false);
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [isPrimaryRoute, navigate, packetId]);

    const onSaveForFooterNavigating = React.useCallback(() => {
        if (isPrimaryRoute) {
            navigate('/manage-packets', { replace: true });
        } else {
            navigate(-1);
        }
    }, [isPrimaryRoute, navigate]);
    /**
     * Handle complete modal action save
     */
    const OnSaveForFooterCompletedModal = React.useCallback(() => {
        setActiveBackNavigation(true);
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                // Utils.handleSuccessResponse(response);
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                isOpen: false,
                            },
                        },
                        action: Action.Replace,
                    },
                });

                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [dispatch, isPrimaryRoute, location, navigate, packetId]);
    /**
     * Handle complete modal action save
     */
    const OnSaveForFooterCompleteModal = React.useCallback(() => {
        setActiveBackNavigation(true);
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                setActiveBackNavigation(false);
                // Utils.handleSuccessResponse(response);
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                isOpen: false,
                            },
                        },
                        action: Action.Replace,
                    },
                });
            })
            .catch((error) => {
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [dispatch, location, packetId]);

    /**
     * Handle complete modal action save
     */
    const OnSaveForFooterCompleteOnNavigating = React.useCallback(() => {
        setActiveBackNavigation(true);
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                setActiveBackNavigation(false);
                // Utils.handleSuccessResponse(response);
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                isOpen: false,
                            },
                        },
                        action: Action.Replace,
                    },
                });
                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [dispatch, isPrimaryRoute, location, navigate, packetId]);

    const OnBackTriggerLockByOperator = React.useCallback(() => {
        setActiveBackNavigation(true);
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                // Utils.handleSuccessResponse(response);
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                isOpen: false,
                            },
                        },
                        action: Action.Replace,
                    },
                });
                if (isPrimaryRoute) {
                    navigate('/manage-packets', { replace: true });
                } else {
                    navigate(-1);
                }
            })
            .catch((error) => {
                setActiveBackNavigation(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    }, [dispatch, isPrimaryRoute, location, navigate, packetId]);
    /**
     * Handle complete modal action save
     */
    const OnSubmitForFooterCompletedModal = React.useCallback(() => {
        const { current: formik } = innerFormik;
        if (formik) {
            const { values } = formik;
            if (isAllowingGroupUpdate) {
                handleFormikUpdateGroup(values);
            } else if (isDocumentHaveUnexpectedType) {
                handleSaveMoveToCategorization();
            } else if (isAllowingProceedReValidation) {
                handleSaveForRevalidation();
            } else if (isAllowRerunOnSavingPacket) {
                handleSaveForReRunPacket();
            } else if (isAllowingRerunGrouping) {
                handleSaveForReRunGrouping();
            } else if (
                isDuplicateDocumentsFound &&
                isUpdateCategory === false
            ) {
                handleSaveForReRunDuplicationPacket();
            } else {
                handleFormikSubmit(values);
            }
        }
    }, [
        handleFormikSubmit,
        handleFormikUpdateGroup,
        handleSaveForReRunDuplicationPacket,
        handleSaveForReRunGrouping,
        handleSaveForReRunPacket,
        handleSaveForRevalidation,
        handleSaveMoveToCategorization,
        isAllowRerunOnSavingPacket,
        isAllowingGroupUpdate,
        isAllowingProceedReValidation,
        isAllowingRerunGrouping,
        isDocumentHaveUnexpectedType,
        isDuplicateDocumentsFound,
        isUpdateCategory,
    ]);
    /**
     * Handle complete modal action fail validation
     */
    const OnFailValidationFromFooter = React.useCallback(() => {
        setSubmited(true);
    }, []);

    const callbackForPromptAlert = React.useCallback(() => {
        const { current: containerRef } = innerMainRef;
        ManagePacketService.patchOperatorAction({
            open: false,
            packetId,
        })
            .then(() => {
                containerRef?.classList.add('patch');
                // Utils.handleSuccessResponse(response);
                navigate('/not-expected-cross-check', {
                    replace: true,
                    state: {
                        ...defaultState,
                        isOpen: false,
                    },
                });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [dispatch, location, packetId]);

    const [, showLeavePrompt] = useLeavePrompt(false, callbackForPromptAlert);
    const [isLeavePage, setLeavePage] = React.useState<boolean>(false);
    usePageLeave(() => {
        setLeavePage(true);
    });
    const handlePatchingOperatorLockWithTimeout = React.useCallback(() => {
        ManagePacketService.patchOperatorAction({
            open: true,
            packetId: packetId ?? '',
        })
            .then(() => {
                dispatch({
                    type: LOCATION_CHANGE,
                    payload: {
                        location: {
                            ...location,
                            state: {
                                ...defaultState,
                                isOpen: true,
                            },
                        },
                    },
                });
                // const { current: modalRef } = innerModalRef;
                // modalRef?.classList.add('patch');
                // Utils.handleSuccessResponse(response);
                // navigate('/not-expected-packet', {
                //     replace: true,
                //     state: {
                //         ...defaultState,
                //         isOpen: false,
                //     },
                // });
                // window.history.state.isOpen = false;
                //               window.history.replaceState({ ba: false }, '');
            })
            .catch(Utils.handleErrorResponse);
    }, [defaultState, dispatch, location, packetId]);
    const [, cancelOperatorLock, resetOperatorLock] = useTimeoutFn(
        handlePatchingOperatorLockWithTimeout,
        500
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const handleMouseEnter = React.useCallback(
        (event: any) => {
            const { current: containerRef } = innerMainRef;

            if (isPointerInside(containerRef, event)) {
                if (containerRef?.classList.contains('patch')) {
                    containerRef.classList.remove('patch');
                    resetOperatorLock();
                }
                setLeavePage(false);
            }
        },
        [resetOperatorLock]
    );
    useUpdateEffect(() => {
        const { current: containerRef } = innerMainRef;
        if (containerRef) {
            if (isLeavePage) {
                containerRef.addEventListener('mouseenter', handleMouseEnter);
            } else {
                containerRef.removeEventListener(
                    'mouseenter',
                    handleMouseEnter
                );
            }
        }
    }, [isLeavePage]);

    // NOTE: this similar to componentWillUnmount()
    React.useEffect(() => {
        if (defaultState && defaultState.isOpen === false) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            })
                .then(() => {
                    // Utils.handleSuccessResponse(response);
                    navigate('/not-expected-cross-check', {
                        replace: true,
                        state: {
                            ...defaultState,
                            isOpen: true,
                        },
                    });
                })
                .catch(Utils.handleErrorResponse);
        }
        cancelOperatorLock();
        showLeavePrompt(true);
        return () => {
            cancelOperatorLock();
            showLeavePrompt(false);
        };
    }, []);
    const socketEvent = React.useMemo(
        () => socketState.socketData.eventName,
        [socketState.socketData.eventName]
    );
    const isTabActive = useSelector(
        (state: any) => state.carryState.visibilityState
    );
    const broadcastPageLeave = useSelector(
        (state: any) => state.broadcastState.broadcastPageLeave
    );
    const [countingTabActive, setCountTabActive] = useState<number>(0);
    const broadcastActiveTabId = useSelector(
        (state: any) => state.broadcastState.broadcastActiveTabId
    );
    const tabId = useSelector((state: any) => state.carryState.tabId);
    useEffect(() => {
        if (
            document.visibilityState !== 'visible' &&
            broadcastPageLeave === false &&
            isTabActive === true
        ) {
            dispatch(CarryAppActions.SetCarryVisibilityState(!isTabActive));
        }
    }, [broadcastPageLeave, isTabActive]);
    useEffect(() => {
        if (
            document.visibilityState !== 'visible' &&
            broadcastPageLeave === false &&
            isTabActive === true &&
            tabId === broadcastActiveTabId
        ) {
            setCountTabActive(1);
        }
    }, []);

    useUpdateEffect(() => {
        if (tabId !== broadcastActiveTabId) {
            setCountTabActive(0);
        }
    }, [broadcastActiveTabId]);
    const handleVisibilityChange = useCallback(() => {
        dispatch(
            CarryAppActions.SetCarryVisibilityState(
                document.visibilityState === 'visible'
            )
        );
    }, []);

    useEvent('visibilitychange', handleVisibilityChange);
    const [isGoingBackBySocket, setGoingBackBySocket] =
        React.useState<boolean>(false);
    const [isGoingBackBySocketSamePacket, setGoingBackBySocketSamePacket] =
        React.useState<boolean>(false);
    React.useEffect(() => {
        if (socketEvent !== null) {
            if (socketEvent === 'operatorAction') {
                if (
                    // eslint-disable-next-line no-underscore-dangle
                    profileState.userInfo._id ===
                        socketState.socketData.operatorId &&
                    isTabActive === false &&
                    location.pathname === '/not-expected-cross-check'
                ) {
                    if (countingTabActive === 0) {
                        dispatch(
                            BroadcastActions.SetBroadcastProgression(true)
                        );
                        setGoingBackBySocketSamePacket(
                            socketState.socketData.packetId === packetId
                        );
                        setGoingBackBySocket(
                            socketState.socketData.packetId !== packetId
                        );
                    } else {
                        setCountTabActive((prev) => {
                            if (prev > 0) {
                                return prev - 1;
                            }
                            return prev;
                        });
                    }
                }
            }
            dispatch(SocketActions.ResetSocketEvent({}));
        }
    }, [socketEvent]);
    const broadcastProgression = useSelector(
        (state: any) => state.broadcastState.broadcastProgression
    );
    useUpdateEffect(() => {
        if (broadcastProgression && isTabActive === true) {
            // Toast.showInfoMessage(
            //     'Another packet is already in open state, closing that packet to opening packet'
            // );
            dispatch(BroadcastActions.SetBroadcastProgression(false));
        }
    }, [broadcastProgression]);

    useEffect(() => {
        if (isActiveBackNavigation) {
            setFreezing(true);
        }
        return () => {
            setFreezing(false);
        };
    }, [isActiveBackNavigation]);

    useUpdateEffect(() => {
        if (isGoingBackBySocketSamePacket && isFreeze === false) {
            dispatch({
                type: LOCATION_CHANGE,
                payload: {
                    location: {
                        ...location,
                        state: {
                            isOpen: false,
                        },
                    },
                    action: Action.Replace,
                },
            });

            if (isPrimaryRoute) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [isGoingBackBySocketSamePacket]);
    useUpdateEffect(() => {
        if (isGoingBackBySocket && isFreeze === false) {
            if (isPrimaryRoute) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [isGoingBackBySocket]);
    const fieldListOnDocument = useMemo(
        () =>
            defaultState && defaultState.documents
                ? Array.from(defaultState.documents).map(
                      (e, i) => `documents[${i}].category`
                  )
                : [],
        [defaultState]
    );

    const handleFormikScrolToView = useCallback(
        (element: Element | null) => {
            if (isToSubmit) {
                setSubmited(false);
                if (element && typeof element.scrollIntoView === 'function') {
                    element?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        },
        [isToSubmit]
    );
    useFormikElementOnValidationEffect(
        (val, element) => {
            if (val === false) {
                handleFormikScrolToView(element);
            }
        },
        fieldListOnDocument,
        innerFormik
    );

    const initialNotExpectedMeta = React.useMemo(
        () => ({
            setActiveBackNavigation,
            isActiveBackNavigation,
            isCommonHighlight,
            setCommonHighlight,
        }),
        [isActiveBackNavigation, isCommonHighlight]
    );

    const [isSendToRework, setSendToRework] = React.useState<boolean>(false);

    const [isActiveReworks, setActiveReworks] = React.useState<boolean>(false);
    const handleActiveReworksItem = React.useCallback(() => {
        setActiveReworks(true);
    }, []);
    const handleDisableReworksItem = React.useCallback(() => {
        setActiveReworks(false);
    }, []);

    const isNotSendToRework = useMemo(
        () =>
            defaultState &&
            ((defaultState.packetStatus === 'Not Expected' &&
                ((isUpdateCategory === false &&
                    isAllowingGroupUpdate === false &&
                    isDocumentHaveUnexpectedType === false &&
                    isAllowingProceedReValidation === false &&
                    isAllowRerunOnSavingPacket === false) ||
                    isDocumentHaveUnexpectedType === true ||
                    (isDuplicateDocumentsFound && showDuplicateDocuments))) ||
                defaultState.packetStatus === 'Fully Duplication' ||
                defaultState.packetStatus === 'Partial Duplication' ||
                defaultState.packetStatus === 'Duplicate - New docs'),
        [
            defaultState,
            isAllowRerunOnSavingPacket,
            isAllowingGroupUpdate,
            isAllowingProceedReValidation,
            isDocumentHaveUnexpectedType,
            isDuplicateDocumentsFound,
            isUpdateCategory,
            showDuplicateDocuments,
        ]
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const missingDocumentCategoryList: RawDocumentClient[] = useMemo(
        () =>
            Array.from(
                defaultState && defaultState.missingDocumentCategoryList
                    ? defaultState.missingDocumentCategoryList
                    : []
            ).map((e) => ({
                group: e.group,
                sendToRework: false,
                statusMessage: '',
                predefinedMessage: '',
                category: e.category,
                _id: '',
                file: '',
                groups: [],
                placeholder: '',
                processedFile: '',
                groupName: '',
                isDuplicate: false,
                variation: '',
                unexpectedFileType: false,
                actions: {
                    uuid: '',
                },
                allowReplaceDocument: false,
                categoryUpdated: false,
            })),
        [location.state]
    );

    useEffect(() => {
        if (isRecordLocale) {
            ManagePacketService.patchOperatorAction({
                open: true,
                packetId,
            });
        }
    }, []);

    return (
        <>
            <HeadTag title="Manage cross check records" />
            <NotExpectedMeta.Provider value={initialNotExpectedMeta}>
                <Formik
                    innerRef={innerFormik}
                    validationSchema={
                        isUpdateCategory || isSendToRework
                            ? NotExpectedPacketScheme
                            : null
                    }
                    initialValues={{
                        missingDocuments: missingDocumentCategoryList,
                        isReworkModal: false,
                        isOpenToSendRework: false,
                        isGroupUpdate: isAllowingGroupUpdate,
                        solicitor: '',
                        reworktext: '',
                        reasonby: '',
                        datepickerRework: dateFns
                            .addDays(new Date(), timeToRework ?? 0)
                            .toString(),
                        documents,
                        duplicationDocuments: documentsDuplication,
                    }}
                    onSubmit={handleFormikSubmit}
                >
                    <FooterWrapper
                        mainContainerRef={innerMainRef}
                        allowRerunGrouping={isAllowingRerunGrouping}
                        allowRevalidation={isAllowingProceedReValidation}
                        canCategorize={isUpdateCategory}
                        Footer={Footer}
                        isLoading={isLoading}
                        onBack={OnBackTriggerLockByOperator}
                        onSubmit={OnSubmitForFooterCompletedModal}
                        onSave={OnSaveForFooterCompletedModal}
                        onFailValidation={OnFailValidationFromFooter}
                        innerModalCompletedRef={completedModalRef}
                        unexpectedType={isDocumentHaveUnexpectedType}
                        updateGroup={isAllowingGroupUpdate}
                        allowRerun={isAllowRerunOnSavingPacket}
                        allowRerunDuplication={isDuplicateDocumentsFound}
                        onModalChanged={(
                            value: boolean,
                            save: boolean,
                            cond: boolean
                        ) => {
                            setSendToRework(value);
                            if (
                                value === false &&
                                save === false &&
                                cond === true
                            ) {
                                OnSaveForFooterCompleteModal();
                            }
                            if (value === false && save && cond) {
                                OnSaveForFooterCompleteOnNavigating();
                            } else if (value === false && save) {
                                onSaveForFooterNavigating();
                            }
                        }}
                        isActiveReworks={isActiveReworks}
                    >
                        <DetailNotExpectedPage
                            onActiveReworksItem={handleActiveReworksItem}
                            onDisableReworksItem={handleDisableReworksItem}
                            groupingFailed={isGroupingFailed}
                            canCategorize={isUpdateCategory}
                            showCategory={showCategory}
                            unexpectedType={isDocumentHaveUnexpectedType}
                            groupingUpdate={isAllowingGroupUpdate}
                            groups={groups}
                            isNotSendToRework={isNotSendToRework}
                        />
                    </FooterWrapper>
                </Formik>
            </NotExpectedMeta.Provider>
        </>
    );
};

export default NotExpectedPage;
